import { useAppInsights } from '@/services/insights/useAppInsights';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';

export const useInvites = (partnerId: string, companyId: string) => {
  return useAPIQuery([`getInvites`, partnerId, companyId], async client => {
    const response = await client.invites.getInvites({ params: { partnerId, companyId } });
    if (response.status !== 200) throw new Error('There is an error');
    return response.body;
  });
};

export const useAnonymousInvite = (id: string) => {
  return useAPIQuery([`getInviteStatus`, id], async client => {
    const response = await client.invites.getInviteStatus({ params: { id } });
    if (response.status !== 200) throw new Error('There is an error');
    return response;
  });
};

export interface InviteToCompanyArgs {
  partnerId: string;
  companyId: string;
  email: string;
}

export const useInviteToCompany = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation(client => async ({ partnerId, companyId, email }: InviteToCompanyArgs) => {
    const response = await client.invites.inviteToCompany({ params: { partnerId, companyId }, body: { email } });
    if (response.status !== 200) throw new Error('Error sending out company invite');
    trackEvent('INVITE:SENT', { inviteId: response.body.id });
    return response.body;
  });
};

export interface InvitesToCustomerArgs {
  partnerId: string;
  companyId: string;
  emails: string[];
}

export const useInvitesToCompany = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation((client, query) => async ({ partnerId, companyId, emails }: InvitesToCustomerArgs) => {
    const emailResponses = await Promise.all(
      emails.map(async email => {
        const response = await client.invites.inviteToCompany({ params: { partnerId, companyId }, body: { email } });
        if (response.status !== 200) throw new Error('Error sending out company invite');
        trackEvent('INVITE:SENT', { inviteId: response.body.id });
        return response.body;
      })
    );

    await query.invalidateQueries([`getCompanyUsers`, partnerId, companyId]);
    return emailResponses;
  });
};

export interface InvitesToCustomersArgs {
  partnerId: string;
  companiesInfo: {
    id: string;
    emails: string[];
  }[];
}

export const useInvitesToCompanies = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation(client => async ({ partnerId, companiesInfo }: InvitesToCustomersArgs) => {
    const emailResponses = await Promise.all(
      companiesInfo.map(async info => {
        const response = await client.invites.invitesToCompany({
          params: { partnerId, companyId: info.id },
          body: info.emails.map(email => {
            return {
              email
            };
          })
        });
        if (response.status !== 200) throw new Error('Error sending out company invites');
        response.body.items.map(invite => trackEvent('INVITE:SENT', { inviteId: invite }));
        return response.body;
      })
    );

    return emailResponses;
  });
};

export interface InviteToPartnerArgs {
  partnerId: string;
  email: string;
}

export const useInviteToPartner = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation(client => async ({ partnerId, email }: InviteToPartnerArgs) => {
    const response = await client.invites.inviteToPartner({ params: { partnerId }, body: { email } });
    if (response.status !== 200) throw new Error('Error sending out partner invite');
    trackEvent('INVITE:SENT', { inviteId: response.body.id });
    return response.body;
  });
};

export interface InvitesToPartnerArgs {
  partnerId: string;
  emails: string[];
}

export const useInvitesToPartner = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation((client, query) => async ({ partnerId, emails }: InvitesToPartnerArgs) => {
    const emailResponses = await Promise.all(
      emails.map(async email => {
        const response = await client.invites.inviteToPartner({ params: { partnerId }, body: { email } });
        if (response.status !== 200) throw new Error('Error sending out partner invite');
        trackEvent('INVITE:SENT', { inviteId: response.body.id });
        return response.body;
      })
    );

    await query.invalidateQueries([`getPartnerUsers`, partnerId]);
    return emailResponses;
  });
};

export const useInviteAdmin = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation(client => async ({ email }: { email: string }) => {
    const response = await client.invites.inviteAsAdmin({ body: { email } });
    if (response.status !== 200) throw new Error('Error sending out admin invite');
    trackEvent('INVITE:SENT', { inviteId: response.body.id });
    return response.body;
  });
};

export const useInviteCreditChecker = () => {
  const trackEvent = useAppInsights(state => state.trackEvent);

  return useAPIMutation(client => async ({ email, name }: { email: string; name: string }) => {
    const codeResponse = await client.credit.createCreditCheckCode({ body: { names: [name] } });
    if (codeResponse.status !== 200) throw new Error('Error creating credit checker code');

    const creditCheckerResponse = await client.credit.createCreditChecker({
      body: { name: name, code: codeResponse.body.items[0].code }
    });
    if (creditCheckerResponse.status !== 200) throw new Error('Error creating credit checker');

    const inviteResponse = await client.invites.inviteCreditChecker({
      body: { email },
      params: { creditCheckerId: creditCheckerResponse.body.id }
    });
    if (inviteResponse.status !== 200) throw new Error('Error sending out credit check invite');
    trackEvent('INVITE:SENT', { inviteId: inviteResponse.body.id });
    return inviteResponse.body;
  });
};

export interface ReleaseCompanyInviteArgs {
  partnerId: string;
  companyId: string;
  inviteId: string;
}

export const useReleaseCompanyInvite = () => {
  return useAPIMutation((client, query) => async ({ partnerId, companyId, inviteId }: ReleaseCompanyInviteArgs) => {
    const response = await client.invites.releaseCompanyInvite({
      params: { partnerId, companyId, inviteId },
      body: {}
    });
    if (response.status !== 200) throw new Error('Error releasing invite');
    
    // Invalidate the cache for invites
    await query.invalidateQueries(['getInvites', partnerId, companyId]);
    return response.body;
  });
};
